import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';

import { toCamelCase, toSnakeCase } from '@/utils/caseConvertor';
import { SCHEDULE_MAX_COUNT, SCHEDULE_TYPES } from '@/utils/constants';

function getScheduleDatesPerDate({
  endDate, dayInterval, maxCount, startDate,
}) {
  const max = maxCount || SCHEDULE_MAX_COUNT;
  const interval = dayInterval || 1;
  const start = startDate ? dayjs(startDate) : dayjs();
  const end = endDate ? dayjs(endDate) : null;

  const dates = [];
  for (let i = 0; i < max; i += 1) {
    const date = start.add(i * interval, 'day');
    if (end && date.isAfter(end, 'day')) {
      break;
    }
    dates.push(date);
  }
  return dates;
}

function getScheduleDatesPerWeekday({
  endDate, weekdayIntervals, maxCount, startDate,
}) {
  const max = maxCount || SCHEDULE_MAX_COUNT;
  const weekdays = (weekdayIntervals || []).sort(); // 0 (Sunday) to 6 (Saturday)
  if (!weekdays.length) {
    return [];
  }

  const start = startDate ? dayjs(startDate) : dayjs();
  const end = endDate ? dayjs(endDate) : null;
  const lap = Math.ceil(max / weekdays.length) + 1;

  const dates = [];
  for (let i = 0; i < lap; i += 1) {
    weekdays.forEach((weekday) => {
      const date = start.add(i * 7, 'day').day(weekday);
      if (dates.length >= max || date.isBefore(start, 'day') || (end && date.isAfter(end, 'day'))) {
        return;
      }
      dates.push(date);
    });
  }
  return dates;
}

export function getScheduleDates(payload = {}) {
  const { type } = payload;
  if (type === SCHEDULE_TYPES.WEEKLY) {
    return getScheduleDatesPerWeekday(payload);
  }
  return getScheduleDatesPerDate(payload);
}

/**
 * see takeorder-groupbuying-scheduler-js/src/v1/index.js
 * type: 'period' | 'weekly' // 週期性 or 每週
 * weekday: [1-7] // 0:星期日, 1:星期一, 6:星期六
 * period: 1 // 每隔幾天開團
 * orderStart: 1 // 領取日前幾天開放訂購
 * orderPeriodDay: 1 // 開放訂購天數
 * orderPeriodTime: ['12:00', '14:00'] // 開放訂購時段
 * orderCreateDay: 1 // 開放前幾天公告團購
 * orderCreateTime: '12:00' // 公告團購時間(寫進db時間)
 * firstReceiveDate: '2024-12-31', // 前端使用
 * lastReceiveDate: '2024-12-31', // 7~90天
 * groupBuyingReceiveTimes: [
 *   ['2024-12-30T12:00:00', '2024-12-30T14:00:00'],
 * ],
 * groupBuyingStartTime: '2024-12-30T12:00:00',
 * departmentIds: [1, 2, 3], // 可訂購部門（首團）
 */
export function toClientSchedule(source) {
  if (isEmpty(source)) {
    return {};
  }

  const copy = toCamelCase(source);
  const {
    weekday,
    period,
    orderPeriodTime,
    orderCreateTime,
    firstReceiveDate,
    lastReceiveDate,
    ...props
  } = copy;

  return {
    ...props,
    dayInterval: period,
    orderCreateTime: orderCreateTime ? dayjs(`${dayjs().format('YYYY/MM/DD')} ${orderCreateTime}`).toDate() : null,
    orderPeriodTime: orderPeriodTime ? {
      start: orderPeriodTime[0] ? dayjs(`${dayjs().format('YYYY/MM/DD')} ${orderPeriodTime[0]}`).toDate() : null,
      end: orderPeriodTime[1] ? dayjs(`${dayjs().format('YYYY/MM/DD')} ${orderPeriodTime[1]}`).toDate() : null,
    } : {},
    receiveDateEnd: lastReceiveDate ? dayjs(lastReceiveDate).toDate() : null,
    receiveDateStart: firstReceiveDate ? dayjs(firstReceiveDate).toDate() : null,
    weekdayIntervals: weekday,
  };
}

export function toServerSchedule(source) {
  if (isEmpty(source)) {
    return {};
  }

  const {
    dayInterval,
    orderCreateTime,
    orderPeriodTime,
    receiveDateEnd,
    receiveDateStart,
    weekdayIntervals,
    ...props
  } = source;

  return {
    ...toSnakeCase({ ...props }),
    first_receive_date: dayjs(receiveDateStart).format(),
    last_receive_date: dayjs(receiveDateEnd).format(),
    order_create_time: dayjs(orderCreateTime).format('HH:mm'),
    order_period_time: [
      orderPeriodTime.start ? dayjs(orderPeriodTime.start).format('HH:mm') : null,
      orderPeriodTime.end ? dayjs(orderPeriodTime.end).format('HH:mm') : null,
    ],
    period: dayInterval,
    weekday: weekdayIntervals,
  };
}

export default {
  getScheduleDates,
  toClientSchedule,
  toServerSchedule,
};
